import { Card, CardHeader, CardBody, Text, Flex, Box } from '@backstage/ui';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { RELATION_HAS_PART, Entity } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { RiStackLine } from '@remixicon/react';
import { EmptyState, Tile } from '../../../../shared/components';
import styles from './relations.module.css';

const HasSystemsContent = ({
  entities,
  loading,
  error,
}: {
  entities: Entity[] | undefined;
  loading: boolean;
  error: Error | undefined;
}) => {
  const { t } = useTranslationRef(appTranslationRef);

  if (loading) {
    return (
      <Flex direction="column" gap="3">
        {[1, 2, 3].map(i => (
          <Box key={i} className={styles.skeleton} style={{ height: '60px' }} />
        ))}
      </Flex>
    );
  }
  if (error) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('hasSystems.error')}
      </Text>
    );
  }
  if (!entities || entities.length === 0) {
    return <EmptyState title={t('hasSystems.empty')} />;
  }
  return (
    <Flex direction="column" gap="3">
      {entities.map(system => {
        const ref = `${system.kind}:${system.metadata.namespace || 'default'}/${system.metadata.name}`;
        return (
          <Tile
            key={ref}
            href={`/catalog/${system.metadata.namespace || 'default'}/${system.kind.toLowerCase()}/${system.metadata.name}`}
            title={system.metadata.title || system.metadata.name}
            description={system.metadata.description}
          />
        );
      })}
    </Flex>
  );
};

export const HasSystemsCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'system',
  });

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiStackLine size={18} className={styles.headerIcon} />
          <Text variant="title-small" weight="bold">
            {t('hasSystems.title')}
          </Text>
          {entities && entities.length > 0 && (
            <Text variant="body-x-small" className={styles.countBadge}>
              {entities.length}
            </Text>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        <HasSystemsContent entities={entities} loading={loading} error={error} />
      </CardBody>
    </Card>
  );
};
